// Zuege: kaufen, bauen, abholen, zuenden.
//
// Ein Zug geht **entweder** an den Server **oder** in den lokalen Spielstand —
// und genau diese Entscheidung ist der einzige Unterschied zwischen Online und
// Solo. Sie steht hier und nicht in der Verdrahtung, damit die Ausnahme des
// Netzes hier bleibt: `try/catch` gehoert um Speicher und Netz, sonst nirgends
// (Gate FG-13).
//
// Online ist der Server autoritativ: seine Antwort **ersetzt** den Spielstand.
// Ein lokales Vorwegnehmen gaebe es nur, damit es sich schneller anfuehlt — und
// kostete jedes Mal die Frage, was gilt, wenn der Server ablehnt.

import { bus } from '../core/bus';
import {
  balancing, booster as boosterById, node as nodeById, perk as perkById,
  recipe as recipeById, sideQuest as sideQuestById,
} from '../data/balancing';
import { sideDone, sideUnlocked } from '../sim/sidequests';
import { affordableRuns, builtIds, nodeCost, nodeUnlocked } from '../sim/stats';
import { buildLocked, buildState } from '../sim/tree';
import { nowSeconds, store } from '../state/store';
import { ApiFehler, type Api, type SavePayload } from './api';
import { COLONY_OPS, applyLocalColonyMove, colonyArgs, type ColonyKind } from './colony';

export type MoveKind =
  | 'node' | 'build' | 'quest' | 'side' | 'booster' | 'perk' | 'convert' | 'ore' | 'wish'
  | 'boostermode' | 'home'
  | ColonyKind;

const COLONY_KINDS = new Set<MoveKind>(Object.keys(COLONY_OPS) as ColonyKind[]);

/** Ist das ein Zug der Kolonie (Akt III)? Er geht dann durch `net/colony.ts`. */
export const isColonyKind = (kind: MoveKind): kind is ColonyKind => COLONY_KINDS.has(kind);

export interface MoveResult {
  ok: boolean;
  /** Grund der Ablehnung, bereits in Spielersprache. */
  message?: string;
}

const ABGELEHNT: MoveResult = { ok: false };

/**
 * Ein Zug zur Zeit.
 *
 * Ohne diese Sperre halten sich beim Halten im Technologiebaum ein Dutzend
 * Anfragen gleichzeitig auf der Leitung. Die aeltere Antwort kann dann **nach**
 * der neueren eintreffen, und `store.adopt` ersetzt bedingungslos — der Spieler
 * saehe seine Ressourcen zurueckspringen.
 *
 * Sie gilt fuer **alle** Zugarten, denn sie schreiben denselben Spielstand.
 * Verworfen wird **still** (ohne `message`): der Spieler hat nichts falsch
 * gemacht, und ein Toast je Klick waere Laerm.
 *
 * „Still" hiess anfangs auch **unsichtbar**, und die Begruendung dafuer war
 * „kuerzer, als ein Mensch zwei Knoepfe nacheinander trifft". Das gilt fuer
 * zwei **verschiedene** Knoepfe. Am Ressourcenkonverter drueckt derselbe
 * Mensch **denselben** Knopf zwanzigmal — ein Rezept liefert eine Einheit,
 * eine Ausbaustufe braucht dutzende —, und dort verschwand die Haelfte seiner
 * Klicks spurlos. Solo kamen alle durch, weil der Zug dort synchron laeuft:
 * genau dieser Unterschied sah aus wie „der Server kommt nicht mit".
 *
 * Deshalb meldet die Sperre jetzt, dass sie zu ist (`move:busy`). Der Knopf
 * zeigt es, der Klick geht weiterhin nicht verloren, sondern findet einen
 * Knopf vor, der sichtbar wartet.
 */
let laufend = false;

/** Welcher Endpunkt zu welcher Zugart gehoert — die eine Weiche. */
function senden(api: Api, kind: MoveKind, id: string, times: number): Promise<SavePayload> {
  if (isColonyKind(kind)) {
    const move = colonyArgs(kind, id, times);
    // Ein Zug, dessen ID nicht passt, geht nicht auf die Leitung — der Server
    // wuerde ihn ohnehin ablehnen, und der Fehler laege dann im Netz statt hier.
    if (!move) return Promise.reject(new ApiFehler('invalid', 'Dieser Zug ist so nicht möglich.'));
    return api.colony(COLONY_OPS[kind], move);
  }
  return kind === 'node' ? api.buyNode(id)
    : kind === 'build' ? api.build(id)
      : kind === 'quest' ? api.claimQuest(id)
      : kind === 'side' ? api.claimSide(id)
        : kind === 'perk' ? api.spendPoint(id)
          : kind === 'convert' ? api.convert(id, times)
            : kind === 'ore' ? api.toggleOre(id)
              : kind === 'wish' ? api.wishOre(id)
              : kind === 'boostermode' ? api.boosterMode(id)
              : kind === 'home' ? api.homeJump()
                : api.buyBooster(id);
}

/**
 * Ein Zug an den Server.
 *
 * `times` traegt nur der Konverter — der Vorgabewert haelt die neun anderen
 * Aufrufer unveraendert. Wie viele Durchlaeufe tatsaechlich gebucht wurden,
 * steht **nicht** in der Antwort: der Server schickt den ganzen Spielstand,
 * und die Verdrahtung misst den Zuwachs (`main.ts`).
 */
export async function applyRemoteMove(
  api: Api, kind: MoveKind, id: string, times = 1,
): Promise<MoveResult> {
  if (laufend) return ABGELEHNT;
  laufend = true;
  // Zwei Rahmen, und die Aufteilung ist der Punkt: der **aeussere** haelt die
  // Sperre gesund und faengt **nichts** — auch die Meldung liegt darin, sonst
  // bliebe `laufend` an einem stolpernden Zuhoerer haengen, und ab da waere
  // jeder weitere Zug still abgelehnt. Der **innere** `catch` liegt exakt um
  // das Netz, und nur darum (Gate FG-13): ein Fehler in `store.adopt` ist ein
  // Denkfehler und darf nicht als „Der Server hat nicht geantwortet"
  // durchgehen.
  try {
    bus.emit('move:busy', { busy: true });
    let payload: SavePayload;
    try {
      payload = await senden(api, kind, id, times);
    } catch (error) {
      return {
        ok: false,
        message: error instanceof ApiFehler ? error.message : 'Der Server hat nicht geantwortet.',
      };
    }
    store.adopt(payload.save, payload.revision);
    return { ok: true };
  } finally {
    laufend = false;
    bus.emit('move:busy', { busy: false });
  }
}

/**
 * Die Solo-Fassung derselben Regeln.
 *
 * Spiegelt `backend/farmining/progression/rules.py`. Dass es zwei Fassungen
 * gibt, ist der Preis dafuer, dass das Spiel ohne Backend spielbar bleibt —
 * und der Grund, warum die Freischalt- und Kostenlogik in `sim/stats.ts`
 * liegt und **nicht** hier: sie wird von beiden Wegen gelesen.
 */
export function applyLocalMove(kind: MoveKind, id: string, times = 1): MoveResult {
  if (isColonyKind(kind)) return applyLocalColonyMove(kind, id, times);
  if (kind === 'node') return buyNode(id);
  if (kind === 'build') return build(id);
  if (kind === 'quest') return claimQuest(id);
  if (kind === 'side') return claimSide(id);
  if (kind === 'perk') return spendPoint(id);
  if (kind === 'convert') return convert(id, times);
  if (kind === 'ore') return toggleOre(id);
  if (kind === 'wish') return setWishOre(id);
  if (kind === 'boostermode') return setBoosterMode(id);
  if (kind === 'home') return homeJump();
  return buyBooster(id);
}

function buyNode(id: string): MoveResult {
  const node = nodeById(id);
  if (!node) return ABGELEHNT;
  const level = store.upgradeLevel(id);
  if (level >= node.max) return { ok: false, message: `${node.name} ist bereits maximal ausgebaut.` };
  if (!nodeUnlocked(
    node, store.save.levels, builtIds(store.save.built), store.save.ship.level,
    store.statCard('enemiesByKind'),
  )) {
    return { ok: false, message: `${node.name} ist noch gesperrt.` };
  }
  if (!store.pay({ [node.cost.res]: nodeCost(node, level) })) {
    return { ok: false, message: 'Die Kosten sind nicht gedeckt.' };
  }
  store.save.levels[id] = level + 1;
  store.count('nodesBought');
  store.refresh();
  return { ok: true };
}

function build(id: string): MoveResult {
  const index = balancing().builds.findIndex((entry) => entry.id === id);
  const target = index < 0 ? undefined : balancing().builds[index];
  if (!target) return ABGELEHNT;
  // Die Kette ist **eine** Folge, die Ära ein Riegel darin: nach dem
  // Teilchenbeschleuniger steht die Bergungswerft schon da und wartet auf den
  // Sprung. Die Begründung kommt aus derselben Auswertung wie die Sperre
  // (`sim/tree.ts`) — dieselbe, aus der das Panel seinen Text nimmt.
  const grund = buildLocked(buildState(store.save, index, target.minEra));
  if (grund) return { ok: false, message: grund };
  if (!store.pay(target.cost)) return { ok: false, message: 'Die Kosten sind nicht gedeckt.' };
  store.save.built++;
  store.count('buildsDone');
  if (target.final) store.save.finished = true;
  store.refresh();
  return { ok: true };
}

function claimQuest(id: string): MoveResult {
  const quest = balancing().quests.find((entry) => entry.id === id);
  if (!quest) return ABGELEHNT;
  if (store.save.quests[id]) return { ok: false, message: `${quest.name} wurde bereits abgeholt.` };
  store.save.quests[id] = true;
  store.count('questsClaimed');
  for (const [res, amount] of Object.entries(quest.reward)) store.add(res, amount);
  return { ok: true };
}

/**
 * Einen erfüllten Nebenauftrag abholen — die Solo-Fassung von `claim_side`.
 *
 * Die Schritte selbst hakt `sim/sidequests.ts` in der Weltszene ab; hier steht
 * nur die Belohnung, und die läuft über `store.add` wie jede andere.
 */
function claimSide(id: string): MoveResult {
  const quest = sideQuestById(id);
  if (!quest) return ABGELEHNT;
  const save = store.save;
  if (save.sideClaimed[id]) return { ok: false, message: `${quest.name} wurde bereits abgeholt.` };
  if (!sideUnlocked(save, quest, builtIds(save.built))) {
    return { ok: false, message: `${quest.name} ist noch nicht freigeschaltet.` };
  }
  if (!sideDone(save, quest)) return { ok: false, message: `${quest.name} ist noch nicht erfüllt.` };

  save.sideClaimed[id] = true;
  store.count('sideClaimed');
  for (const [res, amount] of Object.entries(quest.reward)) store.add(res, amount);
  store.refresh();
  return { ok: true };
}

function spendPoint(id: string): MoveResult {
  const target = perkById(id);
  if (!target) return ABGELEHNT;
  const spent = store.save.perks[id] ?? 0;
  if (spent >= target.max) return { ok: false, message: `${target.name} ist bereits maximal ausgebaut.` };
  if (store.save.ship.points < 1) return { ok: false, message: 'Kein Punkt übrig.' };

  store.save.ship.points -= 1;
  store.save.perks[id] = spent + 1;
  store.refresh();
  return { ok: true };
}

/**
 * Rohstoffe in Verbundsubstanz umwandeln.
 *
 * Es laeuft ueber `pay` **und** `add` — die beiden vorhandenen Pfade. Damit
 * bleibt „Ressourcen sinken nur durch einen bezahlten Kauf" woertlich wahr.
 */
function convert(id: string, times: number): MoveResult {
  const target = recipeById(id);
  if (!target) return ABGELEHNT;
  if (!builtIds(store.save.built).has(target.needBuild)) {
    return { ok: false, message: `${target.name} braucht erst den Ausbau.` };
  }
  // `times` ist eine **Absicht**, keine Zusage: geklemmt wird auf das Machbare
  // (PROGRESS-001). Der Vorrat aendert sich im Hintergrund laufend, solange die
  // Drohnen graben — eine Ablehnung waere fuer den Spieler nicht erklaerbar.
  const laeufe = affordableRuns(store.save.res, target, times);
  if (laeufe <= 0) return { ok: false, message: 'Die Rohstoffe reichen nicht.' };

  const kosten = Object.fromEntries(
    Object.entries(target.inputs).map(([res, menge]) => [res, menge * laeufe]),
  );
  if (!store.pay(kosten)) return { ok: false, message: 'Die Rohstoffe reichen nicht.' };
  store.add(target.outRes, target.outAmount * laeufe);
  return { ok: true };
}

/**
 * Ein Erz aus- oder wieder einschalten.
 *
 * **Nie alle ausschalten.** Ein Filter, der jedes Erz ausschliesst, waere ein
 * Schiff, das nichts mehr abbaut — und der Spieler saehe nur, dass „nichts mehr
 * passiert". Der letzte Haken bleibt darum stehen.
 */
function toggleOre(id: string): MoveResult {
  if (!balancing().resources.some((entry) => entry.id === id)) return ABGELEHNT;
  if (store.save.ignoredOres[id]) {
    delete store.save.ignoredOres[id];
    return { ok: true };
  }
  if (Object.keys(store.save.ignoredOres).length + 1 >= balancing().resources.length) {
    return { ok: false, message: 'Mindestens ein Erz muss abgebaut werden.' };
  }
  store.save.ignoredOres[id] = true;
  // Ein ausgefiltertes Wunscherz wäre ein Widerspruch: bevorzugt holen und
  // zugleich nicht anfassen. Es fällt darum still weg.
  if (store.save.wishOre === id) store.save.wishOre = '';
  return { ok: true };
}

/**
 * Das **Wunscherz** setzen — leer schaltet es ab.
 *
 * Eine **Neigung**, keine Zielauswahl (`GAME-001`): der Spieler wählt, *was*
 * seine Drohnen bevorzugt holen, nicht *welchen* Brocken. Ist keins in
 * Reichweite, graben sie die nächstbesten — es legt sie nie still.
 */
function setWishOre(id: string): MoveResult {
  if (id === '') { store.save.wishOre = ''; return { ok: true }; }
  if (!balancing().resources.some((entry) => entry.id === id)) return ABGELEHNT;
  if (store.save.ignoredOres[id]) {
    return { ok: false, message: 'Ein ausgefiltertes Erz kann kein Wunscherz sein.' };
  }
  store.save.wishOre = id;
  return { ok: true };
}

/**
 * Der Heimflug — die Solo-Fassung derselben Regel.
 *
 * Ein **bezahlter Zug**, kein Spielereignis: der Spieler löst ihn aus, und der
 * Preis stand vorher sichtbar da (`GAME-001`, Gate FG-43). Was mitfliegt, wird
 * hier schlicht nicht angefasst — das ist keine Aufzählung zum Nachpflegen,
 * sondern die Abwesenheit von Code.
 */
function homeJump(): MoveResult {
  if (store.save.era !== balancing().eraFirst) {
    return { ok: false, message: 'Der Sprung ist bereits geschehen.' };
  }
  if (!store.save.finished) {
    return { ok: false, message: 'Der Teilchenbeschleuniger steht noch nicht.' };
  }
  // `pay` lehnt ab, **bevor** irgendetwas anderes passiert: ein ungedeckter
  // Sprung lässt den Spielstand Feld für Feld unberührt.
  if (!store.pay(balancing().homeJumpCost)) {
    return { ok: false, message: 'Die Vorräte reichen für den Sprung nicht.' };
  }
  store.save.era = balancing().eraHome;
  store.count('homeJumps');
  store.refresh();
  return { ok: true };
}

/**
 * Den Schalter setzen: stapeln statt verlängern.
 *
 * Der Wert wird **gesetzt**, nicht umgeschaltet — ein Umschalter verlöre bei
 * einer verlorenen Antwort den Gleichstand zwischen Knopf und Spielstand.
 * Wortgleich zu `rules.set_booster_stacking`.
 */
function setBoosterMode(id: string): MoveResult {
  store.save.boosterStacking = id === 'on';
  store.refresh();
  return { ok: true };
}

/**
 * Einen Booster zünden. **Wortgleich zu `rules.buy_booster`.**
 *
 * Zünden während der Laufzeit verlängert — es sei denn, der Spieler hat auf
 * Stapeln geschaltet. Dann wird er stärker statt länger: jede Zündung legt die
 * Karte noch einmal auf, bis zu `boosterMaxStack`, und das Ende bleibt, wo es
 * war. Das ist die ganze Entscheidung, die der Schalter anbietet — breit oder
 * tief.
 */
function buyBooster(id: string): MoveResult {
  const boost = boosterById(id);
  if (!boost) return ABGELEHNT;
  const now = nowSeconds();
  const laeuft = (store.save.boosterEnds[id] ?? 0) > now;
  const stapeln = store.save.boosterStacking && laeuft;

  // **Am Anschlag wird abgelehnt, nicht geschluckt.** Ein bezahlter Kauf, der
  // nichts tut, ist der teuerste Fehler einer Währungssenke — deshalb steht die
  // Prüfung vor `pay`.
  if (stapeln && (store.save.boosterStacks[id] ?? 1) >= balancing().boosterMaxStack) {
    return {
      ok: false,
      message: `${boost.name} liegt schon ${balancing().boosterMaxStack}fach — mehr geht nicht.`,
    };
  }
  if (!store.pay({ [balancing().boosterCurrency]: boost.cost })) {
    return { ok: false, message: 'Nicht genug Kristalle.' };
  }

  if (stapeln) {
    store.save.boosterStacks[id] = (store.save.boosterStacks[id] ?? 1) + 1;
  } else {
    // Ein **Ende**, kein Restwert: der Ablauf braucht damit keinen Zähler, der
    // ihn bedient, und überlebt jedes Neuladen.
    const ende = Math.max(now, store.save.boosterEnds[id] ?? 0) + boost.dur;
    store.save.boosterEnds[id] = ende;
    store.save.boosterStacks[id] = 1;
    // Das Altfeld mitschreiben, damit `SAVE_VERSION` nicht steigen muss.
    store.save.boosters[id] = ende - now;
  }
  store.count('boostersUsed');
  store.refresh();
  return { ok: true };
}

/** Neu anfangen. Online loescht der Server, solo der Zwischenspeicher. */
export async function applyRemoteWipe(api: Api): Promise<MoveResult> {
  try {
    const payload = await api.wipe();
    store.adopt(payload.save, payload.revision);
    return { ok: true };
  } catch {
    return { ok: false, message: 'Der Server hat nicht geantwortet.' };
  }
}
